import type { WorldObject } from '@/types'

/**
 * Absolute, not relative — the native iOS/Android shells load the bundled
 * app from capacitor://localhost / https://localhost, not from this real
 * domain, so a relative /media/... path would 404 there since no proxy
 * Function runs inside the native webview. Matches scripts/prerender.mjs's
 * own hardcoded SITE_URL for the same reason.
 */
export const MEDIA_ORIGIN = 'https://hellokaleido.net'

const COMMONS_FILE_PATH_RE = /^https:\/\/commons\.wikimedia\.org\/wiki\/Special:FilePath\/([^?]+)\?width=(\d+)$/

/** Rewrites a raw commons.wikimedia.org Special:FilePath URL to the same-origin proxy. Returns the input unchanged if it doesn't match the expected shape. */
export function proxyImageUrl(commonsUrl: string): string {
  const match = COMMONS_FILE_PATH_RE.exec(commonsUrl)
  if (!match) return commonsUrl
  const [, filename, width] = match
  return `${MEDIA_ORIGIN}/media/commons/${filename}?width=${width}`
}

export function withProxiedImages(obj: WorldObject): WorldObject {
  return {
    ...obj,
    heroImage: proxyImageUrl(obj.heroImage),
    thumbnail: proxyImageUrl(obj.thumbnail),
    gallery: obj.gallery?.map(proxyImageUrl),
  }
}

/**
 * Swaps the `width` query param on an already-proxied (this-origin)
 * heroImage/thumbnail/gallery URL — for a display context meaningfully
 * smaller than the canonical 1200px hero, so the browser doesn't have to
 * download+decode a full 1200px-wide JPEG just to show it at a fraction of
 * that size (see DiscoveryCard.tsx's usage). `width` must already be one of
 * worker/mediaRoutes.ts's ALLOWED_COMMONS_WIDTHS — anything else just 400s
 * at the edge instead of silently falling back, so a typo here is loud, not
 * a quietly-broken image. No-ops (returns the input unchanged) for a URL
 * that isn't actually one of our own proxied ones, e.g. if withProxiedImages
 * ever received something already off-Commons.
 */
export function withOverriddenWidth(proxiedUrl: string, width: number): string {
  try {
    const url = new URL(proxiedUrl)
    if (url.origin !== new URL(MEDIA_ORIGIN).origin) return proxiedUrl
    url.searchParams.set('width', String(width))
    return url.toString()
  } catch {
    return proxiedUrl
  }
}
